import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, FileText, ChevronRight, Home, Loader2, Inbox, Info, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  bozza: { label: "Bozza", color: "bg-slate-100 text-slate-700 border-slate-300" },
  inviata: { label: "Inviata", color: "bg-sky-100 text-sky-800 border-sky-300" },
  in_verifica: { label: "In verifica", color: "bg-amber-100 text-amber-800 border-amber-300" },
  documenti_mancanti: { label: "Documenti mancanti", color: "bg-orange-100 text-orange-800 border-orange-300" },
  contratto_approvato: { label: "Firma abilitata", color: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  approvata: { label: "Approvata", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  attestazione_inviata: { label: "Attestazione inviata", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  respinta: { label: "Respinta", color: "bg-red-100 text-red-800 border-red-300" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", {day:"2-digit",month:"2-digit",year:"numeric"}); }
  catch { return iso; }
};

export default function UserDashboard() {
  const nav = useNavigate();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLoginNotice, setShowLoginNotice] = useState(
    () => sessionStorage.getItem("cc_user_show_login_notice") === "1"
  );
  const nome = localStorage.getItem("cc_user_nome") || "";
  const email = localStorage.getItem("cc_user_email") || "";
  const token = localStorage.getItem("cc_user_token");

  const dismissLoginNotice = () => {
    sessionStorage.removeItem("cc_user_show_login_notice");
    setShowLoginNotice(false);
  };

  useEffect(() => {
    if (!token) { nav("/utente/login"); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/user/submissions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubs(r.data?.submissions || []);
      } catch (e) {
        if (e?.response?.status === 401) { localStorage.removeItem("cc_user_token"); nav("/utente/login"); return; }
        toast.error("Impossibile caricare le pratiche");
      } finally { setLoading(false); }
    })();
  }, [token, nav]);

  const logout = () => {
    localStorage.removeItem("cc_user_token");
    localStorage.removeItem("cc_user_nome");
    localStorage.removeItem("cc_user_email");
    nav("/utente/login");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="user-dashboard">
      <div className="max-w-5xl mx-auto p-6 lg:p-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Pannello Associato Unioncasa</p>
            <h1 className="text-3xl font-serif text-slate-900 mt-1">Ciao, {nome || email.split("@")[0]}</h1>
            <p className="text-sm text-slate-600 mt-1">{email}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/attestazione" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-white" data-testid="user-new-btn">
              <Home size={14} /> Nuova pratica
            </Link>
            <button onClick={logout} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-white" data-testid="user-logout-btn">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>

        {showLoginNotice && (
          <div className="mb-6 border border-sky-200 bg-sky-50 rounded-lg p-4 flex items-start gap-3 shadow-sm" data-testid="first-login-notice">
            <Info size={18} className="text-sky-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-medium text-sky-900">Non vedi tutte le pratiche?</p>
              <p className="text-sky-800 text-xs mt-1 leading-relaxed">
                Dopo il primo accesso, effettua <strong>logout</strong> e <strong>login nuovamente</strong> per visualizzare le pratiche attive collegate al tuo account.
              </p>
            </div>
            <button
              onClick={dismissLoginNotice}
              className="p-1 text-sky-700 hover:bg-sky-100 rounded-md flex-shrink-0"
              aria-label="Chiudi avviso"
              data-testid="dismiss-login-notice"
            >
              <X size={16} />
            </button>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
          <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Le tue pratiche ({subs.length})
          </div>
          {loading ? (
            <div className="p-10 text-center text-slate-500 text-sm"><Loader2 className="inline-block animate-spin mr-2" size={16} /> Caricamento…</div>
          ) : subs.length === 0 ? (
            <div className="p-10 text-center" data-testid="user-empty-state">
              <Inbox className="mx-auto text-slate-300 mb-3" size={40} />
              <p className="text-sm text-slate-700 font-medium mb-1">Nessuna pratica trovata</p>
              <p className="text-xs text-slate-500 mb-4">Le tue richieste di attestazione appariranno qui.</p>
              <Link to="/attestazione" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-black">
                Inizia una nuova pratica
              </Link>
            </div>
          ) : (
            <div>
              {subs.map((s) => {
                const st = STATUS_LABELS[s.status] || { label: s.status || "—", color: "bg-slate-100 text-slate-700 border-slate-300" };
                return (
                  <Link
                    key={s.id}
                    to={`/utente/pratica/${s.id}`}
                    className="flex items-center gap-4 px-4 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors"
                    data-testid={`user-sub-${s.id}`}
                  >
                    <FileText size={18} className="text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-slate-900 truncate">{s.indirizzo || "Indirizzo non specificato"}</p>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${st.color}`}>{st.label}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Zona {s.zona || "—"} · {s.tipologia || "—"} · {s.files_count} file · {s.submitted_at ? `inviata ${fmtDate(s.submitted_at)}` : `creata ${fmtDate(s.created_at)}`}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-slate-400" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
